import { useState, useMemo, useEffect, type JSX } from "react";
import yaml from "js-yaml";
import ParagraphItem from "./components/draftPreview/DraftParagraphItem";
import DraftPreviewHeader from "./components/draftPreview/DraftPreviewHeader";
import {
  DraftParseError,
  DraftRawFallback,
  SelectionLimitToast,
} from "./components/draftPreview/DraftPreviewStates";
import { useDraftSelection } from "./components/draftPreview/useDraftSelection";
import type { DraftContentData } from "./components/draftPreview/draftPreviewTypes";

interface DraftContentPreviewProps {
  contentText: string;
}

interface ParsedDraft {
  parsedData: DraftContentData | null;
  parseError: string | null;
}

interface ExpansionState {
  expandedParagraphs: Set<number>;
  toggleParagraph: (index: number) => void;
  toggleAll: (expand: boolean) => void;
}

/** Which paragraphs are open; every paragraph starts expanded. */
function useExpandedParagraphs(parsedData: DraftContentData | null): ExpansionState {
  const [expandedParagraphs, setExpandedParagraphs] = useState<Set<number>>(new Set());

  // Initialize expanded paragraphs to show all expanded by default
  useEffect(() => {
    if (parsedData?.paragraphs) {
      setExpandedParagraphs(new Set(parsedData.paragraphs.map((_, idx) => idx)));
    }
  }, [parsedData]);

  const toggleParagraph = (index: number): void => {
    const newExpanded = new Set(expandedParagraphs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedParagraphs(newExpanded);
  };

  const toggleAll = (expand: boolean): void => {
    if (expand && parsedData?.paragraphs) {
      setExpandedParagraphs(new Set(parsedData.paragraphs.map((_, idx) => idx)));
    } else {
      setExpandedParagraphs(new Set());
    }
  };

  return { expandedParagraphs, toggleParagraph, toggleAll };
}

/**
 * DraftContentPreview Component
 * Renders draft_content.yaml with paragraph-based document structure
 * Supports various paragraph types: markdown, recipients, signatures, advisors_table, contacts_table
 */
export default function DraftContentPreview_Component({
  contentText,
}: DraftContentPreviewProps): JSX.Element {
  const selection = useDraftSelection();

  // Parse YAML and handle errors
  const { parsedData, parseError } = useMemo<ParsedDraft>(() => {
    try {
      const data = yaml.load(contentText) as DraftContentData;
      return { parsedData: data, parseError: null };
    } catch (error) {
      return { parsedData: null, parseError: (error as Error).message };
    }
  }, [contentText]);

  const { expandedParagraphs, toggleParagraph, toggleAll } = useExpandedParagraphs(parsedData);

  if (parseError) {
    return <DraftParseError message={parseError} />;
  }

  // No paragraphs structure - fall back to raw YAML display
  if (!parsedData || !Array.isArray(parsedData.paragraphs)) {
    return <DraftRawFallback contentText={contentText} />;
  }

  // Render structured document paragraphs
  return (
    <div className="h-full overflow-auto bg-cvc-canvas">
      {selection.showLimitWarning && <SelectionLimitToast />}

      <div className="max-w-5xl mx-auto py-6 px-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <DraftPreviewHeader
            paragraphCount={parsedData.paragraphs.length}
            selectedCount={selection.selectedCount}
            onToggleAll={toggleAll}
            onClearSelections={selection.clearSelections}
          />

          <div className="divide-y divide-gray-100">
            {parsedData.paragraphs.map((paragraph, index) => (
              <ParagraphItem
                key={index}
                paragraph={paragraph}
                index={index}
                isExpanded={expandedParagraphs.has(index)}
                onToggle={() => toggleParagraph(index)}
                projectId={selection.projectId}
                docId={selection.docId}
                onToggleSelection={selection.toggleSelection}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
